import { useCallback, useLayoutEffect, useRef, type RefObject } from 'react';

interface ThreadHistoryLoaderOptions {
  viewportRef: RefObject<HTMLElement | null>;
  itemCount: number;
  fetchOlder: () => Promise<unknown>;
}

/** Preserve the reader's visual position when older history is prepended. */
export function useThreadHistoryLoader({ viewportRef, itemCount, fetchOlder }: ThreadHistoryLoaderOptions) {
  const restoreRef = useRef<{ scrollHeight: number; scrollTop: number } | null>(null);

  useLayoutEffect(() => {
    const restore = restoreRef.current;
    const viewport = viewportRef.current;
    if (!restore || !viewport) return;
    viewport.scrollTop = restore.scrollTop + (viewport.scrollHeight - restore.scrollHeight);
    restoreRef.current = null;
  }, [itemCount, viewportRef]);

  const loadOlder = useCallback(async () => {
    const viewport = viewportRef.current;
    if (viewport) {
      restoreRef.current = {
        scrollHeight: viewport.scrollHeight,
        scrollTop: viewport.scrollTop,
      };
    }
    try {
      await fetchOlder();
    } catch (error) {
      restoreRef.current = null;
      throw error;
    }
  }, [fetchOlder, viewportRef]);

  return { loadOlder };
}
